import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2, FlaskConical, Calendar, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import ActionMenu from './ActionMenu';
import { authAPI } from '../services/apiService';
import { canAccessSetup, getStoredStaffUser } from '../utils/roles';
import {
  CASE_FACTORS,
  calculatePricing,
  createDefaultPricingConfig,
  emptyFactorValues,
  getPricingConfigs,
  savePricingConfigs
} from '../services/pricingService';

const blankSimulator = () => ({
  pfBalance: 500000,
  factorValues: {
    ...emptyFactorValues(),
    activation: 1,
    transfers: 1
  },
  finalAgreedFee: '',
  initialAdvance: ''
});

const SETUP_TABS = [
  { id: 'general', label: 'General' },
  { id: 'factors', label: 'Case Factors' },
  { id: 'fees', label: 'Fee Brackets' },
  { id: 'terms', label: 'Quote Terms' },
  { id: 'simulate', label: 'Simulator' }
];

const SIDE_SECTIONS = [
  { id: 'fee', label: 'Fee & Timeline' },
  { id: 'settings', label: 'Settings' }
];

const PricingSettings = () => {
  const staff = getStoredStaffUser();
  const [configs, setConfigs] = useState(() => getPricingConfigs());
  const [selectedId, setSelectedId] = useState(() => getPricingConfigs()[0]?.id);
  const [tab, setTab] = useState('general');
  const [sideSection, setSideSection] = useState('fee');
  const [simulator, setSimulator] = useState(blankSimulator);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'admin'
  });

  const selected = useMemo(
    () => configs.find((cfg) => cfg.id === selectedId) || configs[0],
    [configs, selectedId]
  );

  const simulation = useMemo(() => {
    if (!selected) return null;
    return calculatePricing({
      factorValues: simulator.factorValues,
      pfBalance: simulator.pfBalance,
      finalAgreedFee: simulator.finalAgreedFee === '' ? null : simulator.finalAgreedFee,
      initialAdvance: simulator.initialAdvance === '' ? null : simulator.initialAdvance,
      configOverride: selected
    });
  }, [selected, simulator]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await authAPI.getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (sideSection === 'settings') loadUsers();
  }, [sideSection]);

  const updateSelected = (updater) => {
    setConfigs((prev) => prev.map((cfg) => {
      if (cfg.id !== selected.id) return cfg;
      const next = typeof updater === 'function' ? updater(cfg) : { ...cfg, ...updater };
      return { ...next, updatedAt: new Date().toISOString() };
    }));
  };

  const handleSave = () => {
    const saved = savePricingConfigs(configs);
    setConfigs(saved);
    toast.success('Set up saved. Cases use the version by effective date.');
  };

  const handleAddVersion = () => {
    const next = createDefaultPricingConfig(new Date().toISOString().slice(0, 10));
    next.name = `Setup ${next.effectiveFrom}`;
    const updated = savePricingConfigs([next, ...configs]);
    setConfigs(updated);
    setSelectedId(next.id);
    toast.success('New setup version created');
  };

  const handleDeleteVersion = (id) => {
    if (configs.length <= 1) {
      toast.error('At least one version is required');
      return;
    }
    if (!window.confirm('Delete this setup version?')) return;
    const updated = savePricingConfigs(configs.filter((cfg) => cfg.id !== id));
    setConfigs(updated);
    setSelectedId(updated[0].id);
    toast.success('Version deleted');
  };

  const handleCreateUser = async (e) => {
    e?.preventDefault?.();
    if (!newUser.username?.trim() || !newUser.password) {
      toast.error('Username and password are required');
      return;
    }
    try {
      await authAPI.createUser({
        ...newUser,
        email: newUser.email?.trim() || `${newUser.username.trim()}@pfclaims.local`
      });
      toast.success('Admin user created');
      setNewUser({ username: '', email: '', password: '', role: 'admin' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  if (!canAccessSetup(staff)) {
    return <Navigate to="/adminportal/dashboard" replace />;
  }

  if (!selected) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="page-title">Set up</h1>
          <p className="page-subtitle">
            Fee & timeline settings, and admin user roles (Super Admin only).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sideSection === 'fee' && (
            <>
              <button type="button" onClick={handleAddVersion} className="btn-secondary inline-flex items-center">
                <Plus className="w-4 h-4 mr-2" />
                New Version
              </button>
              <button type="button" onClick={handleSave} className="btn-primary inline-flex items-center">
                <Save className="w-4 h-4 mr-2" />
                Save
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="card-compact xl:col-span-1 space-y-3">
          <div className="space-y-1">
            {SIDE_SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSideSection(s.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                  sideSection === s.id
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-slate-700 hover:bg-primary-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {sideSection === 'fee' && (
            <div className="border-t border-slate-200 pt-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Pricing versions</h3>
              <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                {configs.map((cfg) => (
                  <div
                    key={cfg.id}
                    className={`rounded-lg border p-3 cursor-pointer transition ${
                      cfg.id === selected.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setSelectedId(cfg.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-slate-900 text-sm">{cfg.name}</div>
                        <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Effective {cfg.effectiveFrom}
                        </div>
                      </div>
                      <ActionMenu
                        items={[
                          {
                            label: 'Delete',
                            icon: Trash2,
                            danger: true,
                            onClick: () => handleDeleteVersion(cfg.id)
                          }
                        ]}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sideSection === 'settings' && (
            <p className="text-xs text-slate-500 border-t border-slate-200 pt-3">
              Manage Super Admin and Admin accounts. Members use the member portal only.
            </p>
          )}
        </div>

        <div className="xl:col-span-3 space-y-3">
          {sideSection === 'fee' && (
            <>
          <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-2">
            {SETUP_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  tab === t.id
                    ? 'bg-primary-600 text-white'
                    : 'text-slate-600 hover:bg-primary-50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'general' && (
            <div className="card-compact space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="field-label">Name</label>
                  <input
                    value={selected.name || ''}
                    onChange={(e) => updateSelected({ name: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="field-label">Effective from</label>
                  <input
                    type="date"
                    value={String(selected.effectiveFrom || '').slice(0, 10)}
                    onChange={(e) => updateSelected({ effectiveFrom: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="field-label">Markup % (list fee)</label>
                  <input
                    type="number"
                    value={selected.markupPercent ?? 120}
                    onChange={(e) => updateSelected({ markupPercent: parseFloat(e.target.value) || 0 })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="field-label">Default advance %</label>
                  <input
                    type="number"
                    value={selected.advancePercentDefault ?? 30}
                    onChange={(e) => updateSelected({ advancePercentDefault: parseFloat(e.target.value) || 0 })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="field-label">Working days / month</label>
                  <input
                    type="number"
                    value={selected.workingDaysPerMonth ?? 22}
                    onChange={(e) => updateSelected({ workingDaysPerMonth: parseFloat(e.target.value) || 22 })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="field-label">Timeline buffer (WD)</label>
                  <input
                    type="number"
                    value={selected.timelineBufferDays ?? 5}
                    onChange={(e) => updateSelected({ timelineBufferDays: parseFloat(e.target.value) || 0 })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Timeline = sum(our days × qty) + max(external wait among active factors) + buffer.
                Display uses working days/month (e.g. 1 Month(s), 15 Day(s)).
              </p>
            </div>
          )}

          {tab === 'factors' && (
            <div className="card-compact overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Factor</th>
                    <th className="py-2 px-2">Score wt</th>
                    <th className="py-2 px-2">Our days</th>
                    <th className="py-2 px-2">Wait days</th>
                  </tr>
                </thead>
                <tbody>
                  {CASE_FACTORS.map((f) => (
                    <tr key={f.key} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-medium text-slate-800">{f.label}</td>
                      {['scoreWeights', 'ourDays', 'waitDays'].map((mapKey) => (
                        <td key={mapKey} className="py-2 px-2">
                          <input
                            type="number"
                            min="0"
                            value={selected[mapKey]?.[f.key] ?? 0}
                            onChange={(e) => updateSelected((cfg) => ({
                              ...cfg,
                              [mapKey]: {
                                ...cfg[mapKey],
                                [f.key]: parseFloat(e.target.value) || 0
                              }
                            }))}
                            className="w-20 rounded border border-slate-300 px-2 py-1"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'fees' && (
            <div className="card-compact space-y-2">
              {(selected.feeBrackets || []).map((bracket, index) => (
                <div key={index} className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500 w-16">Min score</span>
                  <input
                    type="number"
                    value={bracket.minScore}
                    onChange={(e) => updateSelected((cfg) => {
                      const feeBrackets = [...cfg.feeBrackets];
                      feeBrackets[index] = { ...feeBrackets[index], minScore: parseFloat(e.target.value) || 0 };
                      return { ...cfg, feeBrackets };
                    })}
                    className="w-24 rounded border border-slate-300 px-2 py-1.5 text-sm"
                  />
                  <select
                    value={bracket.type}
                    onChange={(e) => updateSelected((cfg) => {
                      const feeBrackets = [...cfg.feeBrackets];
                      feeBrackets[index] = { ...feeBrackets[index], type: e.target.value };
                      return { ...cfg, feeBrackets };
                    })}
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm"
                  >
                    <option value="fixed">Fixed ₹</option>
                    <option value="percent">% of PF</option>
                  </select>
                  <input
                    type="number"
                    value={bracket.value}
                    onChange={(e) => updateSelected((cfg) => {
                      const feeBrackets = [...cfg.feeBrackets];
                      feeBrackets[index] = { ...feeBrackets[index], value: parseFloat(e.target.value) || 0 };
                      return { ...cfg, feeBrackets };
                    })}
                    className="w-28 rounded border border-slate-300 px-2 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    className="text-red-500"
                    onClick={() => updateSelected((cfg) => ({
                      ...cfg,
                      feeBrackets: cfg.feeBrackets.filter((_, i) => i !== index)
                    }))}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn-secondary inline-flex items-center text-sm"
                onClick={() => updateSelected((cfg) => ({
                  ...cfg,
                  feeBrackets: [...(cfg.feeBrackets || []), { minScore: 0, type: 'fixed', value: 3000, label: 'Custom' }]
                }))}
              >
                <Plus className="w-4 h-4 mr-1" /> Add bracket
              </button>
            </div>
          )}

          {tab === 'terms' && (
            <div className="card-compact space-y-2">
              {(selected.quoteTerms || []).map((term, index) => (
                <div key={index} className="flex gap-2">
                  <textarea
                    value={term}
                    rows={2}
                    onChange={(e) => updateSelected((cfg) => {
                      const quoteTerms = [...cfg.quoteTerms];
                      quoteTerms[index] = e.target.value;
                      return { ...cfg, quoteTerms };
                    })}
                    className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    className="text-red-500 self-start"
                    onClick={() => updateSelected((cfg) => ({
                      ...cfg,
                      quoteTerms: cfg.quoteTerms.filter((_, i) => i !== index)
                    }))}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => updateSelected((cfg) => ({
                  ...cfg,
                  quoteTerms: [...(cfg.quoteTerms || []), '']
                }))}
              >
                Add term
              </button>
            </div>
          )}

          {tab === 'simulate' && simulation && (
            <div className="card-compact space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <FlaskConical className="w-4 h-4" />
                Quote simulator
              </div>
              <div>
                <label className="field-label">PF balance (₹)</label>
                <input
                  type="number"
                  value={simulator.pfBalance}
                  onChange={(e) => setSimulator((s) => ({ ...s, pfBalance: parseFloat(e.target.value) || 0 }))}
                  className="w-full max-w-xs rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CASE_FACTORS.map((f) => (
                  <label key={f.key} className="flex items-center justify-between gap-2 rounded border border-slate-200 px-2.5 py-1.5 text-sm">
                    <span className="truncate">{f.label}</span>
                    {f.type === 'flag' ? (
                      <input
                        type="checkbox"
                        checked={(simulator.factorValues[f.key] || 0) > 0}
                        onChange={(e) => setSimulator((s) => ({
                          ...s,
                          factorValues: { ...s.factorValues, [f.key]: e.target.checked ? 1 : 0 }
                        }))}
                      />
                    ) : (
                      <input
                        type="number"
                        min="0"
                        className="w-16 rounded border border-slate-300 px-1.5 py-0.5 text-right"
                        value={simulator.factorValues[f.key] || 0}
                        onChange={(e) => setSimulator((s) => ({
                          ...s,
                          factorValues: { ...s.factorValues, [f.key]: parseFloat(e.target.value) || 0 }
                        }))}
                      />
                    )}
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <SimStat label="Score" value={simulation.totalScore} />
                <SimStat label="Suggested" value={`₹${simulation.suggestedFee.toLocaleString('en-IN')}`} />
                <SimStat label="List fee" value={`₹${simulation.listFee.toLocaleString('en-IN')}`} />
                <SimStat label="Timeline" value={simulation.timelineLabel} />
              </div>
              <p className="text-xs text-slate-500">
                Breakdown: our {simulation.timeline.ourDaysTotal} WD + max wait {simulation.timeline.maxExternalWait} WD + buffer {simulation.timeline.bufferDays} WD
                = {simulation.totalWorkingDays} WD ({simulation.timelineShort})
              </p>
            </div>
          )}
            </>
          )}

          {sideSection === 'settings' && (
            <div className="card-compact space-y-4">
              <div className="flex items-start gap-2">
                <Shield className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="section-title mb-1">Admin users</h3>
                  <p className="text-xs text-slate-500">
                    <strong>Super Admin</strong> — full access including Set up and fee/timeline calculators.
                    {' '}<strong>Admin</strong> — Dashboard, Members, Reports only (no Set up / calculators).
                    {' '}<strong>Members</strong> — use the Member Portal only.
                  </p>
                </div>
              </div>

              <form onSubmit={handleCreateUser} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 items-end border border-slate-200 rounded-lg p-3 bg-violet-50/40">
                <div>
                  <label className="field-label">Username</label>
                  <input
                    required
                    value={newUser.username}
                    onChange={(e) => setNewUser((u) => ({ ...u, username: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="field-label">Email (optional)</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="field-label">Password</label>
                  <input
                    required
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="field-label">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser((u) => ({ ...u, role: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="admin">Admin (no Set up)</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <button type="submit" className="btn-primary inline-flex items-center justify-center">
                  <Plus className="w-4 h-4 mr-1" /> Create
                </button>
              </form>

              {usersLoading ? (
                <p className="text-sm text-slate-500 py-6 text-center">Loading users…</p>
              ) : users.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No users found.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 border-b">
                      <th className="py-2 pr-3">Username</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Role</th>
                      <th className="py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-slate-100">
                        <td className="py-2.5 pr-3 font-medium">{u.username}</td>
                        <td className="py-2.5 pr-3 text-slate-600">{u.email}</td>
                        <td className="py-2.5 pr-3">
                          <select
                            value={u.role === 'user' ? 'admin' : u.role}
                            onChange={async (e) => {
                              try {
                                await authAPI.updateUserRole(u.id, e.target.value);
                                toast.success('Role updated');
                                loadUsers();
                              } catch (err) {
                                toast.error(err.response?.data?.message || 'Failed to update role');
                              }
                            }}
                            className="rounded border border-slate-300 px-2 py-1 text-sm"
                          >
                            <option value="admin">Admin</option>
                            <option value="super_admin">Super Admin</option>
                          </select>
                        </td>
                        <td className="py-2.5 text-right">
                          <ActionMenu
                            items={[
                              {
                                label: 'Delete',
                                icon: Trash2,
                                danger: true,
                                onClick: async () => {
                                  if (!window.confirm(`Delete user ${u.username}?`)) return;
                                  try {
                                    await authAPI.deleteUser(u.id);
                                    toast.success('User deleted');
                                    loadUsers();
                                  } catch (err) {
                                    toast.error(err.response?.data?.message || 'Failed to delete user');
                                  }
                                }
                              }
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SimStat = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
    <div className="text-[11px] text-slate-500 uppercase">{label}</div>
    <div className="font-semibold text-slate-900 mt-0.5">{value}</div>
  </div>
);

export default PricingSettings;
